import React, { useState, useEffect } from "react";
import {
  Offcanvas,
  Form,
  Button,
  Alert,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import { Icon } from "../../../utils/iconHelper";
import RubricService, {
  Rubric,
  RubricLevel,
  RubricLeniency,
  CreateRubricRequest,
} from "../../../_services/RubricService";
import "./RubricFormModal.css";

const LENIENCY_OPTIONS: { value: RubricLeniency; label: string; hint: string }[] = [
  { value: "strict", label: "Strict", hint: "Withhold top marks unless every requirement is clearly met." },
  { value: "balanced", label: "Balanced", hint: "Score by best-fit. Minor weaknesses don't disqualify a higher band." },
  { value: "lenient", label: "Lenient", hint: "Reward partial understanding. Round up between adjacent levels." },
];

interface Props {
  show: boolean;
  onHide: () => void;
  rubric: Rubric | null;
  onSuccess: () => void;
}

const RubricFormModal: React.FC<Props> = ({ show, onHide, rubric, onSuccess }) => {
  const [formData, setFormData] = useState<CreateRubricRequest>({
    name: "",
    code: "",
    maxScore: 100,
    criterion: { levels: [] },
  });

  const [activeFlag, setActiveFlag] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoGenerateCode, setAutoGenerateCode] = useState(true);
  // Level indices whose Example textarea is open. A level is treated as open
  // whenever its data carries a non-empty example, so editing an existing
  // rubric reveals what's there.
  const [exampleOpen, setExampleOpen] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (rubric) {
      setFormData({
        name: rubric.name,
        code: rubric.code,
        maxScore: rubric.maxScore,
        criterion: rubric.criterion || { levels: [] },
      });
      setActiveFlag(rubric.activeFlag);
      setAutoGenerateCode(false);
    } else {
      const template = RubricService.getDefaultTemplate();
      setFormData(template);
      setActiveFlag(true);
      setAutoGenerateCode(true);
    }
    setExampleOpen(new Set());
    setError(null);
  }, [rubric, show]);

  const isExampleOpen = (i: number) => {
    if (exampleOpen.has(i)) return true;
    const ex = formData.criterion.levels[i]?.example;
    return !!(ex && ex.trim().length > 0);
  };

  const openExample = (i: number) => {
    setExampleOpen((prev) => {
      const next = new Set(prev);
      next.add(i);
      return next;
    });
  };

  const closeExample = (i: number) => {
    setExampleOpen((prev) => {
      const next = new Set(prev);
      next.delete(i);
      return next;
    });
    // Also clear the value so the level reverts to its base state cleanly.
    handleLevelChange(i, "example", "");
  };

  const handleChange = (field: keyof CreateRubricRequest, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Auto-generate code from name
      if (field === "name" && autoGenerateCode) {
        updated.code = RubricService.generateCode(value);
      }

      return updated;
    });
    setError(null);
  };

  const handleLevelChange = (index: number, field: keyof RubricLevel, value: any) => {
    setFormData((prev) => {
      const levels = [...prev.criterion.levels];
      levels[index] = { ...levels[index], [field]: value };
      return { ...prev, criterion: { ...prev.criterion, levels } };
    });
  };

  const setLeniency = (value: RubricLeniency) => {
    setFormData((prev) => ({
      ...prev,
      criterion: { ...prev.criterion, leniency: value },
    }));
  };

  const addLevel = () => {
    setFormData((prev) => ({
      ...prev,
      criterion: {
        ...prev.criterion,
        levels: [
          ...prev.criterion.levels,
          { level: "", score: 0, description: "" },
        ],
      },
    }));
  };

  const removeLevel = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      criterion: {
        ...prev.criterion,
        levels: prev.criterion.levels.filter((_, i) => i !== index),
      },
    }));
  };

  const moveLevel = (index: number, direction: "up" | "down") => {
    setFormData((prev) => {
      const levels = [...prev.criterion.levels];
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= levels.length) return prev;

      [levels[index], levels[newIndex]] = [levels[newIndex], levels[index]];
      return { ...prev, criterion: { ...prev.criterion, levels } };
    });
  };

  // Max Score is derived from the highest level — kept in sync with the server,
  // which also derives it on save. Shown read-only so authors can't drift the
  // scoring scale away from the levels they defined.
  const derivedMaxScore = formData.criterion.levels.length
    ? Math.max(...formData.criterion.levels.map((l) => Number(l.score) || 0))
    : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate
    const validationError = RubricService.validateRubric(formData);
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      let response;
      if (rubric?.id) {
        response = await RubricService.updateRubric(rubric.id, { ...formData, activeFlag });
      } else {
        response = await RubricService.createRubric(formData);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.message || "Failed to save rubric");
      }
    } catch (err: any) {
      setError(err.message || `Failed to ${rubric ? "update" : "create"} rubric`);
    } finally {
      setLoading(false);
    }
  };

  const sortLevelsByScore = (ascending: boolean = false) => {
    setFormData((prev) => ({
      ...prev,
      criterion: {
        ...prev.criterion,
        levels: [...prev.criterion.levels].sort((a, b) =>
          ascending ? a.score - b.score : b.score - a.score
        ),
      },
    }));
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      style={{ width: "600px" }}
      className="rubric-form-offcanvas"
    >
      <Offcanvas.Header closeButton className="border-bottom py-3 px-4">
        <div className="d-flex align-items-start justify-content-between w-100 gap-3">
          <div className="flex-grow-1">
            <Offcanvas.Title as="h4" className="mb-1 fw-semibold">
              {rubric ? "Edit Rubric" : "Create Rubric"}
            </Offcanvas.Title>
            <div className="text-muted small">
              {rubric
                ? `Update scoring criteria for "${rubric.name}"`
                : "Define scoring levels and calibration for AI-assisted grading"}
            </div>
          </div>
          {rubric && (
            <div
              className="rf-status-toggle"
              role="radiogroup"
              aria-label="Rubric status"
            >
              <button
                type="button"
                role="radio"
                aria-checked={activeFlag}
                className={`rf-status-segment${activeFlag ? " is-selected is-active" : ""}`}
                onClick={() => setActiveFlag(true)}
              >
                <span className="rf-status-dot" />
                Active
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={!activeFlag}
                className={`rf-status-segment${!activeFlag ? " is-selected is-inactive" : ""}`}
                onClick={() => setActiveFlag(false)}
              >
                Inactive
              </button>
            </div>
          )}
        </div>
      </Offcanvas.Header>

      <Form onSubmit={handleSubmit} className="d-flex flex-column h-100 overflow-hidden">
        <Offcanvas.Body className="flex-grow-1 overflow-auto">
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)} className="py-2 px-3 mb-3 small">
              {error}
            </Alert>
          )}

          {/* Basic Info */}
          <div className="rf-section-label">Details</div>
          <div className="mb-3">
            <Form.Label className="small mb-1 fw-medium">Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Essay Writing Rubric"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              required
            />
          </div>

          <div className="row mb-3">
            <div className="col-8">
              <Form.Label className="small mb-1 fw-medium">Code <span className="text-danger">*</span></Form.Label>
              <InputGroup size="sm">
                <Form.Control
                  type="text"
                  placeholder="AUTO_GENERATED_CODE"
                  value={formData.code}
                  onChange={(e) => {
                    setAutoGenerateCode(false);
                    handleChange("code", e.target.value.toUpperCase());
                  }}
                  required
                />
                {!rubric && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setAutoGenerateCode(true);
                      handleChange("code", RubricService.generateCode(formData.name));
                    }}
                    title="Auto-generate from name"
                  >
                    <Icon.RefreshCw size={14} />
                  </Button>
                )}
              </InputGroup>
            </div>
            <div className="col-4">
              <Form.Label className="small mb-1 fw-medium" title="Auto-derived from the highest level score">
                Max Score
              </Form.Label>
              <Form.Control
                type="number"
                size="sm"
                value={derivedMaxScore}
                readOnly
                disabled
                title="Auto-derived from the highest level score"
              />
            </div>
          </div>

          {/* AI Calibration (leniency) */}
          <div className="rf-section-label">AI Calibration</div>
          <div className="mb-3">
            <div className="rf-segmented" role="radiogroup" aria-label="AI calibration">
              {LENIENCY_OPTIONS.map((opt) => {
                const selected =
                  (formData.criterion.leniency || "balanced") === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`rf-segment${selected ? " is-selected" : ""}`}
                    onClick={() => setLeniency(opt.value)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <Form.Text className="text-muted d-block mt-2">
              {
                LENIENCY_OPTIONS.find(
                  (o) => o.value === (formData.criterion.leniency || "balanced")
                )?.hint
              }
            </Form.Text>
          </div>

          {/* Rubric Levels Header */}
          <div className="rf-section-label d-flex justify-content-between align-items-center">
            <span>Scoring Levels ({formData.criterion.levels.length})</span>
            <div className="d-flex gap-1">
              <Button
                variant="secondary"
                size="sm"
                className="py-0 px-2"
                onClick={() => sortLevelsByScore(false)}
                title="Sort by score (high to low)"
              >
                <Icon.ArrowDown size={12} />
              </Button>
              <Button variant="primary" size="sm" className="py-0 px-2" onClick={addLevel}>
                <Icon.Plus size={12} /> Add
              </Button>
            </div>
          </div>

          {/* Levels List */}
          {formData.criterion.levels.length === 0 ? (
            <div className="text-center py-4 text-muted small border rounded bg-light">
              No levels defined. Click "Add" to create grading levels.
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {formData.criterion.levels.map((level, index) => (
                <div key={index} className="rf-level-card">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span
                      className="d-inline-flex align-items-center justify-content-center bg-secondary text-white rounded-circle"
                      style={{ width: "22px", height: "22px", fontSize: "11px", fontWeight: 600 }}
                    >
                      {index + 1}
                    </span>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Level name (e.g., Excellent)"
                      value={level.level}
                      onChange={(e) => handleLevelChange(index, "level", e.target.value)}
                      className="flex-grow-1"
                    />
                    <Form.Control
                      type="number"
                      size="sm"
                      min="0"
                      placeholder="Score"
                      value={level.score}
                      onChange={(e) => handleLevelChange(index, "score", Number(e.target.value))}
                      style={{ width: "70px" }}
                    />
                    <div className="d-flex">
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 px-1 text-muted"
                        onClick={() => moveLevel(index, "up")}
                        disabled={index === 0}
                        title="Move up"
                      >
                        <Icon.ChevronUp size={14} />
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 px-1 text-muted"
                        onClick={() => moveLevel(index, "down")}
                        disabled={index === formData.criterion.levels.length - 1}
                        title="Move down"
                      >
                        <Icon.ChevronDown size={14} />
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 px-1 text-danger rf-level-remove"
                        onClick={() => removeLevel(index)}
                        title="Remove this level"
                      >
                        <Icon.X size={14} />
                      </Button>
                    </div>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    placeholder="Description of this level..."
                    value={level.description}
                    onChange={(e) => handleLevelChange(index, "description", e.target.value)}
                    style={{ resize: "vertical", minHeight: "50px" }}
                  />
                  {isExampleOpen(index) ? (
                    <div className="mt-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="small text-muted">Anchor example</span>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-muted small text-decoration-none"
                          onClick={() => closeExample(index)}
                          title="Remove anchor example"
                        >
                          <Icon.X size={12} /> remove
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        size="sm"
                        placeholder="Sample answer that would earn this score…"
                        value={level.example || ""}
                        onChange={(e) =>
                          handleLevelChange(index, "example", e.target.value)
                        }
                        style={{ resize: "vertical", minHeight: "50px" }}
                      />
                    </div>
                  ) : (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 mt-2 small text-decoration-none"
                      onClick={() => openExample(index)}
                      title="Optional. Strongly improves AI grading consistency."
                    >
                      <Icon.Plus size={12} /> Add anchor example
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Offcanvas.Body>

        {/* Footer */}
        <div className="border-top p-3 bg-light d-flex justify-content-end gap-2">
          <Button variant="secondary" size="sm" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={loading}>
            {loading ? (
              <Spinner role="status" animation="border" size="sm" />
            ) : (
              <>
                <Icon.Save size={14} className="me-1" />
                {rubric ? "Update" : "Create"}
              </>
            )}
          </Button>
        </div>
      </Form>
    </Offcanvas>
  );
};

export default RubricFormModal;
